import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Eye, EyeOff, Mail, Lock, AlertCircle, Shield, X, Search, Users, ToggleLeft, ToggleRight } from 'lucide-react';
import toast from 'react-hot-toast';
import apiClient from '../../services/authService';

const AdminLoginModal = ({ onClose }) => {
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  const {
    register,
    handleSubmit,
    formState: { errors },
    setError
  } = useForm();

  const onSubmit = async (data) => {
    setIsLoading(true);
    try {
      const response = await apiClient.post('/admin/login', data);
      if (response.data) {
        toast.success('Admin login successful!');
        // Store admin token and user data
        localStorage.setItem('adminToken', response.data.accessToken);
        localStorage.setItem('adminUser', JSON.stringify({
          adminUserId: response.data.adminUserId,
          email: response.data.email,
          role: response.data.role
        }));
        
        // Close modal and redirect to admin dashboard
        onClose();
        window.location.href = '/admin/dashboard';
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Admin login failed';
      setError('root', { message: errorMessage });
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = async () => {
    if (!searchTerm.trim()) return;
    
    try {
      const response = await apiClient.post('/admin/customers/search', {
        searchTerm: searchTerm.trim()
      });
      setCustomers(response.data);
    } catch (error) {
      toast.error('Failed to search customers');
    }
  };

  const handleToggleLicense = async (customerId, currentStatus) => {
    try {
      const newStatus = currentStatus === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE';
      const response = await apiClient.put(`/admin/customers/${customerId}/license/toggle`, {
        licenseStatus: newStatus
      });
      
      // Update local state
      setCustomers(prev => prev.map(customer => 
        customer.customerId === customerId 
          ? { ...customer, licenseStatus: newStatus }
          : customer
      ));
      
      toast.success(`License ${newStatus === 'ACTIVE' ? 'activated' : 'deactivated'} successfully`);
    } catch (error) {
      toast.error('Failed to toggle license status');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'ACTIVE': return 'text-green-600 bg-green-100';
      case 'INACTIVE': return 'text-red-600 bg-red-100';
      case 'SUSPENDED': return 'text-yellow-600 bg-yellow-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div className="flex items-center space-x-2">
            <Shield className="h-6 w-6 text-gray-600" />
            <h2 className="text-xl font-semibold text-gray-900">
              {isAuthenticated ? 'Customer License Management' : 'Admin Login'}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
          {!isAuthenticated ? (
            /* Admin Login Form */
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Admin Email
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                  <input
                    {...register('email', {
                      required: 'Email is required',
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: 'Invalid email format'
                      }
                    })}
                    type="email"
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="admin@edelnex.com"
                  />
                </div>
                {errors.email && (
                  <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Password
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                  <input
                    {...register('password', {
                      required: 'Password is required',
                      minLength: {
                        value: 6,
                        message: 'Password must be at least 6 characters'
                      }
                    })}
                    type={showPassword ? 'text' : 'password'}
                    className="w-full pl-10 pr-12 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="Enter admin password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
                {errors.password && (
                  <p className="mt-1 text-sm text-red-600">{errors.password.message}</p>
                )}
              </div>

              {errors.root && (
                <div className="rounded-lg bg-red-50 border border-red-200 p-4">
                  <div className="flex">
                    <AlertCircle className="h-5 w-5 text-red-400" />
                    <div className="ml-3">
                      <p className="text-sm text-red-800">{errors.root.message}</p>
                    </div>
                  </div>
                </div>
              )}

              <button
                type="submit"
                disabled={isLoading}
                className="w-full bg-gray-800 hover:bg-gray-700 text-white py-2 px-4 rounded-lg transition-colors disabled:opacity-50"
              >
                {isLoading ? 'Signing in...' : 'Sign in as Admin'}
              </button>
            </form>
          ) : (
            /* Customer Management Interface */
            <div className="space-y-6">
              {/* Search Section */}
              <div className="flex space-x-4">
                <div className="flex-1">
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search by email, WABA phone, or WABA ID..."
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
                <button
                  onClick={handleSearch}
                  className="bg-primary-600 hover:bg-primary-700 text-white px-6 py-2 rounded-lg transition-colors flex items-center space-x-2"
                >
                  <Search className="h-4 w-4" />
                  <span>Search</span>
                </button>
              </div>

              {/* Customer List */}
              {customers.length > 0 && (
                <div className="space-y-4">
                  <h3 className="text-lg font-medium text-gray-900">Search Results</h3>
                  <div className="space-y-3">
                    {customers.map((customer) => (
                      <div key={customer.customerId} className="bg-gray-50 rounded-lg p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center space-x-4">
                              <div>
                                <p className="font-medium text-gray-900">{customer.customerEmail}</p>
                                <p className="text-sm text-gray-600">WABA: {customer.wabaPhone}</p>
                                <p className="text-sm text-gray-600">ID: {customer.wabaId}</p>
                              </div>
                              <div className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(customer.licenseStatus)}`}>
                                {customer.licenseStatus}
                              </div>
                            </div>
                          </div>
                          <button
                            onClick={() => handleToggleLicense(customer.customerId, customer.licenseStatus)}
                            className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
                              customer.licenseStatus === 'ACTIVE'
                                ? 'bg-red-100 hover:bg-red-200 text-red-700'
                                : 'bg-green-100 hover:bg-green-200 text-green-700'
                            }`}
                          >
                            {customer.licenseStatus === 'ACTIVE' ? (
                              <>
                                <ToggleLeft className="h-4 w-4" />
                                <span>Deactivate</span>
                              </>
                            ) : (
                              <>
                                <ToggleRight className="h-4 w-4" />
                                <span>Activate</span>
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {customers.length === 0 && searchTerm && (
                <div className="text-center py-8">
                  <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-500">No customers found matching your search.</p>
                </div>
              )}

              {!searchTerm && (
                <div className="text-center py-8">
                  <Search className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-500">Enter a search term to find customers.</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminLoginModal;
